/**
 * Side-nav theming helpers. Companies pick a background hex; the foreground is
 * derived as the higher-contrast of two neutrals, and custom-theme backgrounds
 * are validated against WCAG AA at creation time via `meetsSideNavContrast`.
 * The Python equivalent in `webserver/service_actions/side_nav_themes_helper.py`
 * must stay in sync — it injects the same vars on first paint.
 */ "use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
function _export(target, all) {
    for(var name in all)Object.defineProperty(target, name, {
        enumerable: true,
        get: Object.getOwnPropertyDescriptor(all, name).get
    });
}
_export(exports, {
    get PERFORMYARD_LOGO_DARK_URL () {
        return PERFORMYARD_LOGO_DARK_URL;
    },
    get PERFORMYARD_LOGO_LIGHT_URL () {
        return PERFORMYARD_LOGO_LIGHT_URL;
    },
    get SIDE_NAV_DEFAULT_THEMES () {
        return SIDE_NAV_DEFAULT_THEMES;
    },
    get SIDE_NAV_DEFAULT_THEME_ID () {
        return SIDE_NAV_DEFAULT_THEME_ID;
    },
    get SIDE_NAV_FOREGROUND_DARK () {
        return SIDE_NAV_FOREGROUND_DARK;
    },
    get SIDE_NAV_FOREGROUND_LIGHT () {
        return SIDE_NAV_FOREGROUND_LIGHT;
    },
    get SIDE_NAV_MAX_CUSTOM_THEMES () {
        return SIDE_NAV_MAX_CUSTOM_THEMES;
    },
    get WCAG_AA_CONTRAST_RATIO () {
        return WCAG_AA_CONTRAST_RATIO;
    },
    get contrastRatio () {
        return contrastRatio;
    },
    get deriveSideNavForeground () {
        return deriveSideNavForeground;
    },
    get deriveSideNavTheme () {
        return deriveSideNavTheme;
    },
    get isValidHexColor () {
        return isValidHexColor;
    },
    get meetsSideNavContrast () {
        return meetsSideNavContrast;
    },
    get normalizeHexColor () {
        return normalizeHexColor;
    },
    get relativeLuminance () {
        return relativeLuminance;
    }
});
const SIDE_NAV_DEFAULT_THEMES = {
    midnightBlue: {
        id: "midnight-blue",
        name: "Midnight Blue",
        backgroundColor: "#153F6F",
        foregroundColor: "#FFFFFF"
    },
    slate: {
        id: "slate",
        name: "Slate",
        backgroundColor: "#FFFFFF",
        foregroundColor: "#031A38"
    }
};
const SIDE_NAV_DEFAULT_THEME_ID = SIDE_NAV_DEFAULT_THEMES.midnightBlue.id;
const SIDE_NAV_MAX_CUSTOM_THEMES = 3;
const SIDE_NAV_FOREGROUND_LIGHT = "#FFFFFF";
const SIDE_NAV_FOREGROUND_DARK = "#1D1F23";
const PERFORMYARD_LOGO_LIGHT_URL = "/static/img/py-logo/py-logo-and-text-white-medium.png";
const PERFORMYARD_LOGO_DARK_URL = "/static/img/py-logo/py-logo-and-text-medium.png";
const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const isValidHexColor = (hex)=>HEX_RE.test(hex);
const normalizeHexColor = (hex)=>{
    if (!isValidHexColor(hex)) {
        throw new Error(`Invalid hex color: ${hex}`);
    }
    const stripped = hex.slice(1).toUpperCase();
    const expanded = stripped.length === 3 ? stripped.split("").map((c)=>c + c).join("") : stripped;
    return `#${expanded}`;
};
const hexToRgb = (hex)=>{
    const normalized = normalizeHexColor(hex).slice(1);
    const r = Number.parseInt(normalized.slice(0, 2), 16);
    const g = Number.parseInt(normalized.slice(2, 4), 16);
    const b = Number.parseInt(normalized.slice(4, 6), 16);
    return [
        r,
        g,
        b
    ];
};
const channelLuminance = (channel)=>{
    const c = channel / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
};
const relativeLuminance = (hex)=>{
    const [r, g, b] = hexToRgb(hex);
    return 0.2126 * channelLuminance(r) + 0.7152 * channelLuminance(g) + 0.0722 * channelLuminance(b);
};
const contrastRatio = (hexA, hexB)=>{
    const luminanceA = relativeLuminance(hexA);
    const luminanceB = relativeLuminance(hexB);
    const lighter = Math.max(luminanceA, luminanceB);
    const darker = Math.min(luminanceA, luminanceB);
    return (lighter + 0.05) / (darker + 0.05);
};
const deriveSideNavForeground = (backgroundHex)=>contrastRatio(backgroundHex, SIDE_NAV_FOREGROUND_DARK) >= contrastRatio(backgroundHex, SIDE_NAV_FOREGROUND_LIGHT) ? SIDE_NAV_FOREGROUND_DARK : SIDE_NAV_FOREGROUND_LIGHT;
const WCAG_AA_CONTRAST_RATIO = 4.5;
const meetsSideNavContrast = (backgroundHex)=>contrastRatio(backgroundHex, deriveSideNavForeground(backgroundHex)) >= WCAG_AA_CONTRAST_RATIO;
const deriveSideNavTheme = (backgroundHex)=>{
    const backgroundColor = normalizeHexColor(backgroundHex);
    return {
        backgroundColor,
        foregroundColor: deriveSideNavForeground(backgroundColor)
    };
};
